import React from "react";
import { Box, Stack, Typography, Container } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { Clock, Heart, Award } from "lucide-react";

const DifferentiatorsContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  background: "transparent",
  padding: { xs: "60px 0", md: "80px 0" },
  position: "relative",
  overflow: "hidden",
  paddingTop: "70px",
}));

// Single Main Card with Gradient Background
const MainCard = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #E8F4FF 0%, #D6E9FF 50%, #C5DEFF 100%)",
  borderRadius: "34px",
  padding: { xs: "40px 24px", sm: "50px 32px", md: "60px 48px" },
  boxShadow: "0 8px 32px rgba(70, 116, 217, 0.2)",
  border: "1px solid rgba(70, 116, 217, 0.15)",
  margin: { xs: "0 16px", md: "0 auto" },
  maxWidth: "90%",
  margin : "auto",
  padding : "30px",
}));

// Image Item Container - No background, just the image and text
const ImageItem = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  flex: 1,
}));

// Laptop Icon Container
const LaptopIconContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "220px",
  marginBottom: { xs: "16px", md: "20px" },
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

// Laptop Screen
const LaptopScreen = styled(Box)(({ theme }) => ({
  width: "100%",
  aspectRatio: "16/10",
  border: "12px solid #2C3E50",
  borderBottom: "8px solid #2C3E50",
  borderRadius: "12px 12px 0 0",
  backgroundColor: "#FFFFFF",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
}));

// Icon Circle
const IconCircle = styled(Box)(({ theme }) => ({
  width: "70px",
  height: "70px",
  borderRadius: "50%",
  background: "linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: "0 4px 12px rgba(29, 71, 164, 0.2)",
}));

// Laptop Base
const LaptopBase = styled(Box)(({ theme }) => ({
  width: "115%",
  height: "16px",
  backgroundColor: "#2C3E50",
  borderRadius: "0 0 16px 16px",
  margin: "0 -7.5%",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: "0",
    left: "50%",
    transform: "translateX(-50%)",
    width: "30%",
    height: "4px",
    backgroundColor: "#1A252F",
    borderRadius: "2px",
  },
}));

// Shadow
const LaptopShadow = styled(Box)(({ theme }) => ({
  width: "90%",
  height: "8px",
  background: "radial-gradient(ellipse, rgba(0,0,0,0.15) 0%, transparent 70%)",
  margin: "8px auto 0",
}));

const DifferentiatorsSection = () => {
  return (
    <DifferentiatorsContainer>
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        {/* Single Card with Gradient Background */}
        <MainCard
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          {/* Heading - Inside the card */}
          <Box
            sx={{
              textAlign: "center",
              mb: { xs: 3, md: 4 },
            }}
          >
            <Typography
              sx={{
                color: "#282D4D",
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "24px", sm: "32px", md: "42px" },
                fontWeight: 700,
                mb: 2,
                lineHeight: { xs: "32px", md: "1.2" },
              }}
            >
              What Makes{" "}
              <Box component="span" sx={{ color: "#4674D9" }}>
                Our Reports Different?
              </Box>
            </Typography>

            <Typography
              sx={{
                color: "#2E2E2F",
                fontFamily: "Inter, sans-serif",
                fontSize: { xs: "12px", md: "16px" },
                fontWeight: 400,
                textAlign: "center",
                maxWidth: "900px",
                margin: "0 auto",
                lineHeight: { xs: "20px", md: "24px" },
                px: { xs: 1, md: 0 },
              }}
            >
              We combine medical intelligence, human-centered design, and data visualization to help both doctors and patients understand health in seconds, not hours.
            </Typography>
          </Box>

          {/* Three Images with Titles - Inside the card */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={{ xs: 4, md: 4 }}
            sx={{
              mt: { xs: 4, md: 6 },
              justifyContent: "space-around",
              alignItems: "flex-start",
            }}
          >
            {/* First Card - Reduced interpretation time */}
            <ImageItem
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(33.333% - 16px)" },
                minWidth: { xs: "100%", sm: "200px" },
              }}
            >
              {/* Laptop Icon - No background card, just the image */}
              <LaptopIconContainer>
                <Box sx={{ position: "relative", width: "100%" }}>
                  <LaptopScreen>
                    <IconCircle>
                      <Clock size={36} color="#1D47A4" strokeWidth={2.5} />
                    </IconCircle>
                  </LaptopScreen>
                  <LaptopBase />
                  <LaptopShadow />
                </Box>
              </LaptopIconContainer>

              {/* Title Text - Below the image */}
              <Typography
                sx={{
                  color: "#1D47A4",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: { xs: "13px", md: "16px" },
                  fontWeight: 500,
                  lineHeight: { xs: "20px", md: "24px" },
                  px: { xs: 1, md: 2 },
                }}
              >
                Reduced interpretation time for doctors
              </Typography>
            </ImageItem>

            {/* Second Card - Improved satisfaction & trust */}
            <ImageItem
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.45 }}
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(33.333% - 16px)" },
                minWidth: { xs: "100%", sm: "200px" },
              }}
            >
              {/* Laptop Icon - No background card, just the image */}
              <LaptopIconContainer>
                <Box sx={{ position: "relative", width: "100%" }}>
                  <LaptopScreen>
                    <IconCircle>
                      <Heart size={36} color="#1D47A4" strokeWidth={2.5} />
                    </IconCircle>
                  </LaptopScreen>
                  <LaptopBase />
                  <LaptopShadow />
                </Box>
              </LaptopIconContainer>

              {/* Title Text - Below the image */}
              <Typography
                sx={{
                  color: "#1D47A4",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: { xs: "13px", md: "16px" },
                  fontWeight: 500,
                  lineHeight: { xs: "20px", md: "24px" },
                  px: { xs: 1, md: 2 },
                }}
              >
                Improved satisfaction & trust for patients
              </Typography>
            </ImageItem>

            {/* Third Card - Stronger brand perception */}
            <ImageItem
              component={motion.div}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              sx={{
                flex: { xs: "1 1 100%", sm: "1 1 calc(33.333% - 16px)" },
                minWidth: { xs: "100%", sm: "200px" },
              }}
            >
              {/* Laptop Icon - No background card, just the image */}
              <LaptopIconContainer>
                <Box sx={{ position: "relative", width: "100%" }}>
                  <LaptopScreen>
                    <IconCircle>
                      <Award size={36} color="#1D47A4" strokeWidth={2.5} />
                    </IconCircle>
                  </LaptopScreen>
                  <LaptopBase />
                  <LaptopShadow />
                </Box>
              </LaptopIconContainer>

              {/* Title Text - Below the image */}
              <Typography
                sx={{
                  color: "#1D47A4",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: { xs: "13px", md: "16px" },
                  fontWeight: 500,
                  lineHeight: { xs: "20px", md: "24px" },
                  px: { xs: 1, md: 2 },
                }}
              >
                Stronger brand perception for diagnostic centers & hospitals
              </Typography>
            </ImageItem>
          </Stack>
        </MainCard>
      </Container>
    </DifferentiatorsContainer>
  );
};

export default DifferentiatorsSection;
